import {
  ClockDriftError,
  MERGE_FIELDS,
  clockWall,
  compareClocks,
  completeOnce,
  isResting,
  maxClock,
  planInsert,
  sortByRank,
  wakeAt,
  type Band,
  type Card,
  type CardFields,
  type Clock,
  type Conflict,
  type Energy,
  type FieldPatch,
  type HlcState,
  type MergeField,
  type Motivation,
  type MoveCause,
  type PatchFields,
} from '@hmawiw/shared';
import { useMemo } from 'react';
import { create } from 'zustand';
import { getDb, setDb } from '../db/handle';
import { clearAccountData, deleteOutbox, openStorage, readMeta, readSnapshot, setMeta, writeCards, writeConflicts, type Db, type OutboxEntry } from '../db/idb';
import { MOTIVATION_LEVELS, motivationFromFour, remapMotivation } from '@hmawiw/shared';
import { isLate, startOfDay, toTimeString, withTime } from '../lib/dates';
import { snapshotOf, useEvents } from './events';
import { newId } from '../lib/ids';
import { clockState, initClock, receive, tick } from './clock';

/** Board id used before the first sign-in; adoptBoard re-keys these cards to the account's board. */
export const LOCAL_BOARD = 'local';

export interface NewCardInput {
  title: string;
  notes?: string | null;
  firstStep?: string | null;
  motivation: Motivation;
  band: Band;
  energy: Energy;
  dueAt?: number | null;
  /** Not sorted yet: the card waits in the inbox tray; motivation and band are placeholders. */
  inbox?: boolean;
  /** Kept on this device only until keepLocal; never pushed. The tour's example cards. */
  local?: boolean;
}

interface CardEdit {
  id: string;
  changes: Partial<CardFields>;
}

export interface RemoteConflict extends Conflict {
  dismissedAt: number | null;
}

export interface OutboxAck {
  id: string;
  /** Clock per field as it was sent; a field edited again since keeps waiting. */
  sent: Partial<Record<MergeField, Clock>>;
}

export interface CardsState {
  hydrated: boolean;
  deviceId: string;
  boardId: string;
  /** The realm on screen; new cards land there and the board and Journal show only it. Null before sign-in. */
  currentBoardId: string | null;
  cards: Record<string, Card>;
  conflicts: Record<string, Conflict>;
  outbox: Record<string, OutboxEntry>;
  dismissQueue: string[];
  /** Ids of cards that exist on this device only (see NewCardInput.local). */
  localIds: string[];
  hydrate(db?: Db): Promise<void>;
  createCard(input: NewCardInput): Promise<Card>;
  updateCard(id: string, changes: Partial<CardFields>): Promise<void>;
  /**
   * Places a card at `index` of a cell. `among` ranks it against a list other than the cell's
   * own cards (the Horizon view mixes bands), `changes` rides in the same edit.
   */
  moveCard(id: string, motivation: Motivation, band: Band, index: number, extra?: { among?: Card[]; changes?: Partial<CardFields>; cause?: MoveCause }): Promise<void>;
  cycleEnergy(id: string): Promise<void>;
  markDone(id: string): Promise<void>;
  /** Done on a recurring card or Touch on an ongoing one: logged, and the due date moves on. The card stays open. */
  touch(id: string): Promise<void>;
  /** Ends a rest early: the card is due today, at the time it already had, and shows at once. */
  wake(id: string): Promise<void>;
  restore(id: string): Promise<void>;
  remove(id: string): Promise<void>;
  /** Brings a deleted card back to the top of its cell. */
  undelete(id: string): Promise<void>;
  dismissConflict(id: string): Promise<void>;
  /** Server data enters only here; it never writes the outbox. */
  applyRemoteCards(remote: Card[]): Promise<void>;
  applyRemoteConflicts(rows: RemoteConflict[]): Promise<void>;
  ackOutbox(acks: OutboxAck[], drops: string[]): Promise<void>;
  clearDismissals(ids: string[]): Promise<void>;
  adoptBoard(boardId: string): Promise<void>;
  setCurrentBoard(id: string | null): void;
  /** The column count changed: every card keeps its place on the new scale. */
  remapColumns(from: number, to: number): Promise<void>;
  /** Sends a card to another of the user's boards, at the top of the same cell there. */
  moveToBoard(id: string, boardId: string): Promise<void>;
  /** Local-only cards become ordinary synced cards. */
  keepLocal(ids: string[]): Promise<void>;
  /** Local-only cards vanish for good: they never reached the server, so no tombstone is needed. */
  discardLocal(ids: string[]): Promise<void>;
  /** After a server wipe: every card goes back into the outbox as a full patch. */
  requeueAll(): Promise<void>;
  /** After a purge resync: cards the server no longer has, and that are not waiting to be pushed, go. */
  pruneMissing(serverIds: Set<string>): Promise<void>;
  reset(): Promise<void>;
}

/** Records written by a build with a single `reminderAt` get the list form on the way in. */
function normalizeCard(input: Card & { reminderAt?: number | null }): Card {
  // Fields added after the record was stored get their defaults; IndexedDB has no row migration.
  const raw = { ...input, firstStep: input.firstStep ?? null, attempts: Array.isArray(input.attempts) ? input.attempts : [], repeat: input.repeat ?? null, doneLog: Array.isArray(input.doneLog) ? input.doneLog : [], dread: input.dread ?? null, inbox: input.inbox === true };
  if (Array.isArray(raw.reminders)) return raw;
  const { reminderAt, ...rest } = raw;
  const clocks = { ...rest.clocks } as Record<string, Clock>;
  if (clocks['reminderAt']) {
    clocks['reminders'] = clocks['reminderAt'] as Clock;
    delete clocks['reminderAt'];
  }
  return { ...rest, reminders: reminderAt ? [{ kind: 'at', at: reminderAt }] : [], clocks } as Card;
}

function normalizeEntry(entry: OutboxEntry): OutboxEntry {
  const fields = entry.fields as Record<string, FieldPatch | undefined>;
  const old = fields['reminderAt'];
  if (!old) return entry;
  const { reminderAt: _dropped, ...rest } = fields;
  const at = old.value as number | null;
  return { ...entry, fields: { ...rest, reminders: { ...old, value: at ? [{ kind: 'at', at }] : [] } } as PatchFields };
}

function byId<T extends { id: string }>(items: T[]): Record<string, T> {
  const out: Record<string, T> = {};
  for (const item of items) out[item.id] = item;
  return out;
}

export function isAlive(card: Card): boolean {
  return card.deletedAt === null;
}

export function cellCards(cards: Record<string, Card>, motivation: Motivation, band: Band, boardId?: string): Card[] {
  return sortByRank(
    Object.values(cards).filter((c) => isAlive(c) && c.status === 'open' && !c.inbox && c.motivation === motivation && c.band === band && (boardId === undefined || c.boardId === boardId)),
  );
}

/** Cards waiting in the inbox tray, newest first. */
export function inboxCards(cards: Record<string, Card>): Card[] {
  return openCards(cards)
    .filter((c) => c.inbox)
    .sort((a, b) => b.createdAt - a.createdAt);
}

/** The cards of one board, for the parts of the app that show one realm at a time. */
export function ofBoard(cards: Record<string, Card>, boardId: string | null): Record<string, Card> {
  if (boardId === null) return cards;
  const out: Record<string, Card> = {};
  for (const c of Object.values(cards)) if (c.boardId === boardId) out[c.id] = c;
  return out;
}

/** The current board's cards as a stable record; the same object while nothing changed. */
export function useBoardCards(): Record<string, Card> {
  const cards = useCards((s) => s.cards);
  const boardId = useCards((s) => s.currentBoardId);
  return useMemo(() => ofBoard(cards, boardId), [cards, boardId]);
}

/** Open cards, including those resting between two occurrences. */
export function openCards(cards: Record<string, Card>): Card[] {
  return Object.values(cards).filter((c) => isAlive(c) && c.status === 'open');
}

/** The cards the board shows now: open, sorted into a column, and not resting. */
export function boardCards(cards: Record<string, Card>, now: number): Card[] {
  return openCards(cards).filter((c) => !c.inbox && !isResting(c, now));
}

/** Recurring cards between two occurrences, the soonest to return first. */
export function restingCards(cards: Record<string, Card>, now: number): Card[] {
  return openCards(cards)
    .filter((c) => isResting(c, now))
    .sort((a, b) => (wakeAt(a) ?? 0) - (wakeAt(b) ?? 0));
}

/** Tombstones still on this device, newest deletion first. */
export function deletedCards(cards: Record<string, Card>): Card[] {
  return Object.values(cards)
    .filter((c) => c.deletedAt !== null)
    .sort((a, b) => (b.deletedAt ?? 0) - (a.deletedAt ?? 0));
}

export function doneCards(cards: Record<string, Card>): Card[] {
  return Object.values(cards)
    .filter((c) => isAlive(c) && c.status === 'done')
    .sort((a, b) => (b.doneAt ?? 0) - (a.doneAt ?? 0));
}

function fullPatch(card: Card): OutboxEntry {
  const fields: Record<string, FieldPatch> = {};
  for (const f of MERGE_FIELDS) {
    const clock = card.clocks[f];
    if (clock) fields[f] = { value: card[f], clock, base: null };
  }
  return { id: card.id, boardId: card.boardId, createdAt: card.createdAt, isNew: true, fields: fields as PatchFields };
}

function noteRemoteClocks(cards: Card[]): void {
  for (const card of cards) {
    let best: Clock | null = null;
    for (const f of MERGE_FIELDS) best = maxClock(best, card.clocks[f]);
    if (!best) continue;
    try {
      receive(best);
    } catch (e) {
      if (!(e instanceof ClockDriftError)) throw e;
      console.warn('ignoring a clock too far in the future', best);
    }
  }
}

export const useCards = create<CardsState>((set, get) => {
  async function mutateMany(edits: CardEdit[]): Promise<void> {
    const db = getDb();
    const { cards, outbox } = get();
    const now = Date.now();
    const nextCards: Card[] = [];
    const nextOutbox: OutboxEntry[] = [];
    for (const { id, changes } of edits) {
      const card = cards[id];
      if (!card) continue;
      const pending = outbox[id];
      const clocks = { ...card.clocks };
      const fields: Record<string, FieldPatch> = { ...(pending?.fields ?? {}) };
      let updatedAt = card.updatedAt;
      for (const f of MERGE_FIELDS) {
        if (!(f in changes)) continue;
        const clock = tick(now);
        // A field already waiting in the outbox keeps the base the device last synced.
        const base = pending && f in pending.fields ? (pending.fields[f] as FieldPatch).base : (card.clocks[f] ?? null);
        fields[f] = { value: changes[f], clock, base };
        clocks[f] = clock;
        updatedAt = Math.max(updatedAt, clockWall(clock));
      }
      nextCards.push({ ...card, ...changes, clocks, updatedAt });
      nextOutbox.push({
        id,
        boardId: card.boardId,
        createdAt: card.createdAt,
        isNew: pending?.isNew ?? false,
        fields: fields as PatchFields,
      });
    }
    if (nextCards.length === 0) return;
    await writeCards(db, nextCards, nextOutbox, clockState());
    set((s) => ({ cards: { ...s.cards, ...byId(nextCards) }, outbox: { ...s.outbox, ...byId(nextOutbox) } }));
  }

  return {
    hydrated: false,
    deviceId: '',
    boardId: LOCAL_BOARD,
    currentBoardId: null,
    cards: {},
    conflicts: {},
    outbox: {},
    dismissQueue: [],
    localIds: [],

    async hydrate(given) {
      const db = given ?? (await openStorage());
      setDb(db);
      const snap = await readSnapshot(db);
      const storedDevice = snap.meta['deviceId'];
      const deviceId = typeof storedDevice === 'string' ? storedDevice : newId();
      if (storedDevice !== deviceId) await setMeta(db, 'deviceId', deviceId);
      initClock(deviceId, snap.meta['hlc'] as HlcState | undefined);
      const boardId = typeof snap.meta['boardId'] === 'string' ? (snap.meta['boardId'] as string) : LOCAL_BOARD;
      const dismissQueue = Array.isArray(snap.meta['dismissQueue']) ? (snap.meta['dismissQueue'] as string[]) : [];
      const localIds = Array.isArray(snap.meta['localCards']) ? (snap.meta['localCards'] as string[]) : [];
      let cards = snap.cards.map(normalizeCard);
      let outbox = snap.outbox.map(normalizeEntry);
      // Records written when the board had four columns map onto three, once, cards and pending patches alike.
      if (snap.meta['motivationLevels'] !== MOTIVATION_LEVELS) {
        cards = cards.map((c) => ({ ...c, motivation: motivationFromFour(c.motivation) }));
        outbox = outbox.map((e) => {
          const m = e.fields.motivation;
          return m ? { ...e, fields: { ...e.fields, motivation: { ...m, value: motivationFromFour(m.value) } } } : e;
        });
        await writeCards(db, cards, outbox, null);
        await setMeta(db, 'motivationLevels', MOTIVATION_LEVELS);
      }
      set({
        hydrated: true,
        deviceId,
        boardId,
        cards: byId(cards),
        conflicts: byId(snap.conflicts),
        outbox: byId(outbox),
        dismissQueue,
        localIds,
      });
    },

    async createCard(input) {
      const db = getDb();
      const { cards, currentBoardId } = get();
      const boardId = currentBoardId ?? get().boardId;
      const now = Date.now();
      const others = cellCards(cards, input.motivation, input.band, boardId);
      const id = newId();
      const plan = planInsert(others, 0, id);
      const values: CardFields = {
        title: input.title.trim(),
        notes: input.notes?.trim() || null,
        firstStep: input.firstStep?.trim() || null,
        energy: input.energy,
        dueAt: input.dueAt ?? null,
        reminders: [],
        repeat: null,
        dread: null,
        status: 'open',
        deletedAt: null,
        motivation: input.motivation,
        band: input.band,
        rank: plan.rank,
        doneAt: null,
        touchedAt: null,
        attempts: [],
        doneLog: [],
        inbox: input.inbox === true,
      };
      const clocks: Card['clocks'] = {};
      const fields: Record<string, FieldPatch> = {};
      for (const f of MERGE_FIELDS) {
        const clock = tick(now);
        clocks[f] = clock;
        fields[f] = { value: values[f], clock, base: null };
      }
      const card: Card = { ...values, id, boardId, createdAt: now, updatedAt: now, clocks };
      const entry: OutboxEntry = { id, boardId, createdAt: now, isNew: true, fields: fields as PatchFields };
      if (input.local) {
        const localIds = [...get().localIds, id];
        await writeCards(db, [card], [], clockState());
        await setMeta(db, 'localCards', localIds);
        set((s) => ({ cards: { ...s.cards, [id]: card }, localIds }));
      } else {
        await writeCards(db, [card], [entry], clockState());
        set((s) => ({ cards: { ...s.cards, [id]: card }, outbox: { ...s.outbox, [id]: entry } }));
      }
      if (plan.renormalized) {
        await mutateMany(plan.renormalized.filter((r) => r.id !== id).map((r) => ({ id: r.id, changes: { rank: r.rank } })));
      }
      if (!input.local) await useEvents.getState().log('created', id, { ...snapshotOf(card, now), inbox: card.inbox });
      return card;
    },

    updateCard(id, changes) {
      const filtered: Partial<CardFields> = {};
      for (const f of MERGE_FIELDS) {
        if (f in changes) (filtered as Record<MergeField, unknown>)[f] = changes[f];
      }
      return mutateMany([{ id, changes: filtered }]);
    },

    async moveCard(id, motivation, band, index, extra) {
      const card = get().cards[id];
      if (!card) return;
      const others = (extra?.among ?? cellCards(get().cards, motivation, band, card.boardId)).filter((c) => c.id !== id);
      const plan = planInsert(others, index, id);
      // Any placement sorts a card out of the inbox.
      const edits: CardEdit[] = [{ id, changes: { ...(card.inbox ? { inbox: false } : {}), ...(extra?.changes ?? {}), motivation, band, rank: plan.rank } }];
      if (plan.renormalized) {
        for (const r of plan.renormalized) if (r.id !== id) edits.push({ id: r.id, changes: { rank: r.rank } });
      }
      await mutateMany(edits);
      if (card.motivation !== motivation || card.band !== band || card.inbox) {
        await useEvents.getState().log('moved', id, { ...snapshotOf({ motivation, band, energy: card.energy }, Date.now()), from: card.motivation, fromBand: card.band, cause: card.inbox ? 'inbox' : (extra?.cause ?? 'drag') });
      }
    },

    cycleEnergy(id) {
      const card = get().cards[id];
      if (!card) return Promise.resolve();
      const energy = (card.energy === 3 ? 1 : card.energy + 1) as Energy;
      return mutateMany([{ id, changes: { energy } }]);
    },

    async markDone(id) {
      const card = get().cards[id];
      if (!card) return;
      const now = Date.now();
      await mutateMany([{ id, changes: { status: 'done', doneAt: now } }]);
      await useEvents.getState().log('done', id, {
        ...snapshotOf(card, now),
        createdAt: card.createdAt,
        dueAt: card.dueAt,
        late: card.dueAt !== null && isLate(card.dueAt, now),
        hadStep: card.firstStep !== null,
        attempts: card.attempts.length,
      });
    },

    async touch(id) {
      const card = get().cards[id];
      if (!card) return;
      const now = Date.now();
      await mutateMany([{ id, changes: completeOnce(card, now) }]);
      await useEvents.getState().log('touched', id, {
        ...snapshotOf(card, now),
        dueAt: card.dueAt,
        late: card.dueAt !== null && isLate(card.dueAt, now),
        ongoing: card.repeat?.kind === 'ongoing',
      });
    },

    wake(id) {
      const card = get().cards[id];
      if (!card || card.dueAt === null) return Promise.resolve();
      const now = Date.now();
      const time = toTimeString(card.dueAt);
      return mutateMany([{ id, changes: { dueAt: withTime(startOfDay(now), time === '00:00' ? null : time) } }]);
    },

    async restore(id) {
      const card = get().cards[id];
      if (!card) return;
      const others = cellCards(get().cards, card.motivation, card.band, card.boardId);
      const plan = planInsert(others, 0, id);
      const edits: CardEdit[] = [{ id, changes: { status: 'open', doneAt: null, rank: plan.rank } }];
      if (plan.renormalized) for (const r of plan.renormalized) if (r.id !== id) edits.push({ id: r.id, changes: { rank: r.rank } });
      await mutateMany(edits);
      await useEvents.getState().log('undone', id, {});
    },

    async remove(id) {
      const card = get().cards[id];
      if (!card) return;
      const now = Date.now();
      await mutateMany([{ id, changes: { deletedAt: now } }]);
      await useEvents.getState().log('deleted', id, snapshotOf(card, now));
    },

    async undelete(id) {
      const card = get().cards[id];
      if (!card || card.deletedAt === null) return;
      const others = cellCards(get().cards, card.motivation, card.band, card.boardId);
      const plan = planInsert(others, 0, id);
      const edits: CardEdit[] = [{ id, changes: { deletedAt: null, rank: plan.rank } }];
      if (plan.renormalized) for (const r of plan.renormalized) if (r.id !== id) edits.push({ id: r.id, changes: { rank: r.rank } });
      await mutateMany(edits);
      await useEvents.getState().log('restored', id, snapshotOf(card, Date.now()));
    },

    async dismissConflict(id) {
      const db = getDb();
      const queue = [...get().dismissQueue, id];
      await writeConflicts(db, [], [id]);
      await setMeta(db, 'dismissQueue', queue);
      set((s) => {
        const conflicts = { ...s.conflicts };
        delete conflicts[id];
        return { conflicts, dismissQueue: queue };
      });
    },

    async applyRemoteCards(remote) {
      if (remote.length === 0) return;
      const db = getDb();
      const { cards, outbox } = get();
      const merged: Card[] = [];
      for (const raw of remote) {
        // A server older than this client leaves newer fields out; they get their defaults.
        const r = normalizeCard(raw);
        const local = cards[r.id];
        if (!local) {
          merged.push(r);
          continue;
        }
        const pending = outbox[r.id];
        const values: Record<string, unknown> = { ...local };
        const clocks: Card['clocks'] = { ...local.clocks };
        let changed = false;
        for (const f of MERGE_FIELDS) {
          const rc = r.clocks[f];
          if (!rc) continue;
          const lc = clocks[f];
          if (lc && compareClocks(lc, rc) >= 0) continue;
          // A pending local write with a lower clock loses to the server; the push will report it.
          values[f] = r[f];
          clocks[f] = rc;
          changed = true;
        }
        if (!changed && local.boardId === r.boardId) continue;
        merged.push({ ...(values as unknown as Card), boardId: r.boardId, createdAt: r.createdAt, clocks, updatedAt: Math.max(local.updatedAt, r.updatedAt) });
        void pending;
      }
      noteRemoteClocks(remote);
      if (merged.length === 0) return;
      await writeCards(db, merged, [], clockState());
      set((s) => ({ cards: { ...s.cards, ...byId(merged) } }));
    },

    async applyRemoteConflicts(rows) {
      if (rows.length === 0) return;
      const db = getDb();
      const queue = new Set(get().dismissQueue);
      const upserts: Conflict[] = [];
      const deletes: string[] = [];
      for (const row of rows) {
        const { dismissedAt, ...conflict } = row;
        if (dismissedAt !== null || queue.has(row.id)) deletes.push(row.id);
        else upserts.push(conflict);
      }
      await writeConflicts(db, upserts, deletes);
      set((s) => {
        const conflicts = { ...s.conflicts, ...byId(upserts) };
        for (const id of deletes) delete conflicts[id];
        return { conflicts };
      });
    },

    async ackOutbox(acks, drops) {
      const db = getDb();
      const { outbox } = get();
      const keep: OutboxEntry[] = [];
      const gone: string[] = [...drops];
      for (const ack of acks) {
        const entry = outbox[ack.id];
        if (!entry) continue;
        const fields: Record<string, FieldPatch> = {};
        for (const f of MERGE_FIELDS) {
          const fp = entry.fields[f] as FieldPatch | undefined;
          if (!fp) continue;
          const sentClock = ack.sent[f];
          if (sentClock && sentClock === fp.clock) continue; // acknowledged
          // Edited again while in flight: the server now holds what was sent, so that is the new base.
          fields[f] = { ...fp, base: sentClock ?? fp.base };
        }
        if (Object.keys(fields).length === 0) gone.push(ack.id);
        else keep.push({ ...entry, isNew: false, fields: fields as PatchFields });
      }
      if (gone.length) await deleteOutbox(db, gone);
      if (keep.length) await writeCards(db, [], keep, null);
      set((s) => {
        const next = { ...s.outbox, ...byId(keep) };
        for (const id of gone) delete next[id];
        return { outbox: next };
      });
    },

    async clearDismissals(ids) {
      const db = getDb();
      const done = new Set(ids);
      const queue = get().dismissQueue.filter((id) => !done.has(id));
      await setMeta(db, 'dismissQueue', queue);
      set({ dismissQueue: queue });
    },

    async remapColumns(from, to) {
      const edits: CardEdit[] = [];
      for (const c of Object.values(get().cards)) {
        const m = remapMotivation(c.motivation, from, to);
        if (m !== c.motivation) edits.push({ id: c.id, changes: { motivation: m } });
      }
      if (edits.length) await mutateMany(edits);
    },

    setCurrentBoard(id) {
      if (get().currentBoardId !== id) set({ currentBoardId: id });
    },

    async moveToBoard(id, boardId) {
      const db = getDb();
      const { cards, outbox } = get();
      const card = cards[id];
      if (!card || card.boardId === boardId) return;
      const others = cellCards(cards, card.motivation, card.band, boardId);
      const plan = planInsert(others, 0, id);
      const now = Date.now();
      const clock = tick(now);
      const pending = outbox[id];
      // The move rides on the rank field: a patch must carry one, and the card lands at the top of its cell over there.
      const fields = { ...(pending?.fields ?? {}), rank: { value: plan.rank, clock, base: pending && 'rank' in pending.fields ? (pending.fields.rank as FieldPatch).base : (card.clocks.rank ?? null) } } as PatchFields;
      const next: Card = { ...card, boardId, rank: plan.rank, clocks: { ...card.clocks, rank: clock }, updatedAt: Math.max(card.updatedAt, clockWall(clock)) };
      const entry: OutboxEntry = { id, boardId, createdAt: card.createdAt, isNew: pending?.isNew ?? false, fields };
      await writeCards(db, [next], [entry], clockState());
      set((s) => ({ cards: { ...s.cards, [id]: next }, outbox: { ...s.outbox, [id]: entry } }));
      if (plan.renormalized) await mutateMany(plan.renormalized.filter((r) => r.id !== id).map((r) => ({ id: r.id, changes: { rank: r.rank } })));
    },

    async adoptBoard(boardId) {
      const db = getDb();
      const { cards, outbox } = get();
      const changedCards = Object.values(cards)
        .filter((c) => c.boardId === LOCAL_BOARD)
        .map((c) => ({ ...c, boardId }));
      const changedOutbox = Object.values(outbox)
        .filter((e) => e.boardId === LOCAL_BOARD)
        .map((e) => ({ ...e, boardId }));
      if (changedCards.length || changedOutbox.length) await writeCards(db, changedCards, changedOutbox, null);
      await setMeta(db, 'boardId', boardId);
      set((s) => ({ boardId, cards: { ...s.cards, ...byId(changedCards) }, outbox: { ...s.outbox, ...byId(changedOutbox) } }));
    },

    async keepLocal(ids) {
      const db = getDb();
      const { cards, localIds } = get();
      const entries = ids.filter((id) => cards[id] && localIds.includes(id)).map((id) => fullPatch(cards[id] as Card));
      const remaining = localIds.filter((id) => !ids.includes(id));
      if (entries.length) await writeCards(db, [], entries, null);
      await setMeta(db, 'localCards', remaining);
      set((s) => ({ outbox: { ...s.outbox, ...byId(entries) }, localIds: remaining }));
    },

    async discardLocal(ids) {
      const db = getDb();
      const { localIds } = get();
      const gone = ids.filter((id) => localIds.includes(id));
      if (gone.length === 0) return;
      const tx = db.transaction(['cards'], 'readwrite');
      for (const id of gone) void tx.objectStore('cards').delete(id);
      await tx.done;
      const remaining = localIds.filter((id) => !gone.includes(id));
      await setMeta(db, 'localCards', remaining);
      set((s) => {
        const next = { ...s.cards };
        for (const id of gone) delete next[id];
        return { cards: next, localIds: remaining };
      });
    },

    async requeueAll() {
      const db = getDb();
      const { cards, localIds } = get();
      const entries = Object.values(cards)
        .filter((c) => !localIds.includes(c.id))
        .map(fullPatch);
      if (entries.length) await writeCards(db, [], entries, null);
      set({ outbox: byId(entries) });
    },

    async pruneMissing(serverIds) {
      const db = getDb();
      const { cards, outbox, localIds } = get();
      const gone = Object.keys(cards).filter((id) => !serverIds.has(id) && !outbox[id] && !localIds.includes(id));
      if (gone.length === 0) return;
      const tx = db.transaction(['cards', 'conflicts'], 'readwrite');
      for (const id of gone) void tx.objectStore('cards').delete(id);
      const conflicts = await tx.objectStore('conflicts').getAll();
      for (const c of conflicts) if (gone.includes(c.cardId)) void tx.objectStore('conflicts').delete(c.id);
      await tx.done;
      set((s) => {
        const next = { ...s.cards };
        for (const id of gone) delete next[id];
        const nextConflicts = Object.fromEntries(Object.entries(s.conflicts).filter(([, c]) => !gone.includes(c.cardId)));
        return { cards: next, conflicts: nextConflicts };
      });
    },

    async reset() {
      const db = getDb();
      await clearAccountData(db);
      set({ localIds: [], boardId: LOCAL_BOARD, currentBoardId: null, cards: {}, conflicts: {}, outbox: {}, dismissQueue: [] });
    },
  };
});

export async function readLastSeq(): Promise<number> {
  return (await readMeta<number>(getDb(), 'lastSeq')) ?? 0;
}
